import { useEffect, useState } from "react";
import ServiceDeskAPIService from "@sd/services/APIs";
import type { Attachment } from "@sd/types/ticket";
import { collectAttachmentMediaPaths, collectAttachmentMediaPathsFromRecord, getCachedBlobObjectUrl, getCachedPdfBlobObjectUrl, isAttachmentMediaBlocked, prefetchVideoBlob, unwrapFileRecord } from "@sd/lib/attachmentMediaUrl";
import { mimeFromAttachmentFilename } from "@sd/lib/parseAttachmentIds";

function uploadResponseToAttachment(
  id: string,
  res: Record<string, unknown>
): Attachment | null {
  const paths = collectAttachmentMediaPaths({ ...res, id }).filter(
    p => !/thumbnail|thumb|_tn\b|\/tn\//i.test(p)
  );
  const downloadPath = `/file-utility/files/${id}/download/`;
  const url =
    (paths.includes(downloadPath) ? downloadPath : paths[0]) ?? "";
  const thumbnailUrl = collectAttachmentMediaPaths({ ...res, id }).find(p =>
    /thumbnail|thumb/i.test(p)
  );
  if (!url) {
    return null;
  }
  const nameRaw =
    res.file_original_name ??
    res.original_name ??
    res.filename ??
    res.name ??
    "";
  const name = String(nameRaw || url.split("/").pop() || "Attachment");
  const ext = name.split(".").pop()?.toLowerCase() ?? "";
  const type = String(
    res.file_type ?? res.mime_type ?? mimeFromAttachmentFilename(name)
  );
  const size = Number(res.file_size ?? res.size ?? 0) || 0;
  // Preserve the raw absolute https:// file_path as a direct media URL fallback
  const rawFilePath = String(res.file_path ?? res.url ?? "").trim();
  const mediaUrl =
    rawFilePath && rawFilePath !== downloadPath ? rawFilePath : undefined;
  return {
    id,
    name,
    size,
    type,
    url,
    ...(thumbnailUrl ? { thumbnailUrl } : {}),
    ...(mediaUrl ? { mediaUrl } : {}),
    uploadedBy: String(res.uploaded_by ?? res.created_by ?? "—"),
    uploadedAt: String(res.created_at ?? res.uploaded_at ?? ""),
    visibility: "public",
  };
}

/**
 * Hydrate `Attachment` rows from upload file UUIDs (same as admin ticket detail).
 */
export function useAttachmentFilesByIds(ids: string[] | undefined): {
  attachments: Attachment[];
  loading: boolean;
} {
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [loading, setLoading] = useState(false);
  const key = ids?.length ? [...ids].sort().join("|") : "";

  useEffect(() => {
    if (!ids?.length) {
      setAttachments([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    let cancelled = false;
    void Promise.all(
      ids.map(fileId =>
        ServiceDeskAPIService.files.getById({ id: fileId })
          .then((res: unknown) => {
            const record = unwrapFileRecord(
              res && typeof res === "object"
                ? (res as Record<string, unknown>)
                : null
            );
            if (!record) {
              return null;
            }
            return uploadResponseToAttachment(fileId, record);
          })
          .catch(() => null)
      )
    ).then(rows => {
      if (!cancelled) {
        const valid = rows.filter(Boolean) as Attachment[];
        setAttachments(valid);
        setLoading(false);
        // Warm blob cache while cards mount so previews appear faster.
        valid.forEach(attachment => {
          if (isAttachmentMediaBlocked(attachment.id)) {
            return;
          }
          const paths = collectAttachmentMediaPathsFromRecord(attachment, false).filter(
            p => !/thumbnail|thumb|_tn\b|\/tn\//i.test(p)
          );
          if (paths.length === 0) {
            return;
          }
          const isVideo =
            /\.(webm|mp4|ogg|mov|avi)$/i.test(attachment.name) ||
            (attachment.type ?? "").toLowerCase().startsWith("video/");
          const isPdf =
            /\.pdf$/i.test(attachment.name) ||
            (attachment.type ?? "").toLowerCase().includes("pdf");
          if (isVideo) {
            prefetchVideoBlob(attachment.id, attachment.name, paths);
            return;
          }
          if (isPdf) {
            void getCachedPdfBlobObjectUrl(
              paths,
              `pdf:${attachment.id}`,
              undefined,
              attachment.name
            );
            return;
          }
          void getCachedBlobObjectUrl(paths, `${attachment.id}:full`);
        });
      }
    });
    return () => {
      cancelled = true;
    };
  }, [key]);

  return { attachments, loading };
}
